class RegionalFreeShipping {
    constructor() {
        this.storageKey = 'regional-free-shipping-region';
        this.cacheDuration = 24 * 60 * 60 * 1000;
        this.containers = [];
        this.regionConfig = {};
        this.currentRegion = null;
        this.regionDetected = false;
        this.initialized = false;
        this.observer = null;
        this.observerTarget = null;
        this.renderFrame = null;
        this.lastLoggedRegion = null;
    }

    init() {
        this.refreshContainers();

        if (this.containers.length) {
            this.setConfigFromContainer(this.containers[0]);
            this.startObservers();
            this.scheduleRender();
        } else {
            this.setConfigFromContainer(null);
            this.startObservers();
        }

        if (this.regionDetected) {
            return;
        }

        const cachedRegion = this.getCachedRegion();

        if (cachedRegion?.region) {
            this.setRegion(cachedRegion.region, cachedRegion);
            return;
        }

        this.detectRegionFromIp();
    }

    refreshContainers() {
        this.containers = Array.from(document.querySelectorAll('[data-regional-shipping]'));
        return this.containers;
    }

    setConfigFromContainer(container) {
        const parseThreshold = (value, fallback) => {
            const normalized = String(value ?? '')
                .replace(/[^\d,.-]/g, '')
                .replace(',', '.');
            const parsed = Number(normalized);

            return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
        };

        this.regionConfig = {
            sul: {
                label: 'Sul',
                threshold: parseThreshold(container?.dataset?.thresholdSouthSoutheast, 400)
            },
            sudeste: {
                label: 'Sudeste',
                threshold: parseThreshold(container?.dataset?.thresholdSouthSoutheast, 400)
            },
            'centro-oeste': {
                label: 'Centro-Oeste',
                threshold: parseThreshold(container?.dataset?.thresholdMidwest, 600)
            },
            norte: {
                label: 'Norte',
                threshold: parseThreshold(container?.dataset?.thresholdNorthNortheast, 800)
            },
            nordeste: {
                label: 'Nordeste',
                threshold: parseThreshold(container?.dataset?.thresholdNorthNortheast, 800)
            }
        };
    }

    ensureConfig() {
        this.refreshContainers();
        this.setConfigFromContainer(this.containers[0] || null);
    }

    startObservers() {
        if (!this.initialized) {
            document.addEventListener('cart:updated', this.handleCartUpdated);
            document.addEventListener('cart:refresh', this.handleCartUpdated);
            document.body?.addEventListener('cart:refresh', this.handleCartUpdated);
            window.addEventListener('theme:cart:updated', this.handleCartUpdated);
            window.addEventListener('theme:drawer:right:opened', this.handleCartUpdated);
            this.initialized = true;
        }

        const drawer = document.querySelector('.cart--root[data-view="drawer"]');

        if (!drawer) {
            return;
        }

        if (this.observer && this.observerTarget === drawer) {
            return;
        }

        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        this.observerTarget = drawer;

        this.observer = new MutationObserver((mutations) => {
            let shouldRender = false;

            mutations.forEach((mutation) => {
                if (shouldRender) {
                    return;
                }

                if (mutation.target instanceof Element && mutation.target.closest('[data-regional-shipping]')) {
                    return;
                }

                mutation.addedNodes.forEach((node) => {
                    if (shouldRender || !(node instanceof Element)) {
                        return;
                    }

                    if (node.matches('[data-regional-shipping], .cart--totals, .cart--body')
                        || node.querySelector('[data-regional-shipping], .cart--totals, .cart--body')) {
                        shouldRender = true;
                    }
                });
            });

            if (shouldRender) {
                this.scheduleRender();
            }
        });

        this.observer.observe(drawer, {
            childList: true,
            subtree: true
        });
    }

    handleCartUpdated = () => {
        window.setTimeout(() => {
            this.startObservers();
            this.syncTotalsThenRender();
        }, 150);
    }

    syncTotalsThenRender = async () => {
        this.refreshContainers();

        if (!this.containers.length) {
            this.forceRender();
            return;
        }

        try {
            const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin'
            });

            if (response.ok) {
                const cart = await response.json();
                const totalCents = String(cart?.total_price ?? 0);

                this.containers.forEach((container) => {
                    container.setAttribute('data-cart-total-cents', totalCents);
                });
            }
        } catch (error) {
            this.debug(error);
        }

        this.forceRender();
    }

    scheduleRender() {
        if (this.renderFrame !== null) {
            return;
        }

        this.renderFrame = window.requestAnimationFrame(() => {
            this.renderFrame = null;
            this.render();
        });
    }

    forceRender() {
        if (this.renderFrame !== null) {
            window.cancelAnimationFrame(this.renderFrame);
            this.renderFrame = null;
        }

        this.render();
    }

    render() {
        this.refreshContainers();

        if (!this.containers.length) {
            return;
        }

        this.setConfigFromContainer(this.containers[0]);

        this.containers.forEach((container) => {
            this.renderContainer(container);
        });
    }

    renderContainer(container) {
        const message = container.querySelector('[data-regional-shipping-message]');
        const bar = container.querySelector('[data-regional-shipping-bar]');
        const hasRegion = this.regionDetected && this.regionConfig[this.currentRegion];

        container.hidden = !hasRegion;

        if (!hasRegion) {
            return;
        }

        const region = this.regionConfig[this.currentRegion];
        const total = this.getCartTotal(container);
        const remaining = Math.max(0, region.threshold - total);
        const progress = region.threshold > 0
            ? Math.min(100, (total / region.threshold) * 100)
            : 0;

        if (message) {
            if (remaining > 0) {
                message.innerHTML = `Faltam <strong>R$ ${this.formatMoney(remaining)}</strong> para ganhar <strong>frete grátis</strong>.`;
                message.classList.remove('is-loading');
            } else {
                message.innerHTML = 'Parabens! Voce ganhou <strong>frete gratis</strong>.';
                message.classList.remove('is-loading');
            }
        }

        if (bar) {
            bar.style.setProperty('--progress-percent', `${progress}%`);
            if (!bar.hasAttribute('data-transition-active')) {
                bar.setAttribute('data-transition-active', 'true');
            }
        }
    }

    getCartTotal(container) {
        const totalCents = Number(container?.dataset?.cartTotalCents ?? 0);

        if (Number.isFinite(totalCents) && totalCents >= 0) {
            return totalCents / 100;
        }

        return 0;
    }

    formatMoney(value) {
        return Number(value || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    mapStateToRegion(state) {
        const normalizedState = String(state || '').trim().toUpperCase();
        const regions = {
            sul: ['RS', 'SC', 'PR'],
            sudeste: ['SP', 'RJ', 'MG', 'ES'],
            'centro-oeste': ['MT', 'MS', 'GO', 'DF'],
            norte: ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
            nordeste: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE']
        };

        for (const [regionKey, states] of Object.entries(regions)) {
            if (states.includes(normalizedState)) {
                return regionKey;
            }
        }

        return 'sudeste';
    }

    setRegionFromState(state, metadata = {}) {
        const region = this.mapStateToRegion(state);

        this.setRegion(region, {
            source: metadata.source || 'state',
            state,
            zip: metadata.zip || ''
        });
    }

    setRegion(region, metadata = {}) {
        this.ensureConfig();

        if (!this.regionConfig[region]) {
            return;
        }

        this.currentRegion = region;
        this.regionDetected = true;
        this.cacheRegion(region, metadata);
        this.logRegion(region, metadata);
        this.forceRender();
    }

    logRegion(region, metadata = {}) {
        if (metadata.source !== 'cep' && this.lastLoggedRegion === region) {
            return;
        }

        this.lastLoggedRegion = region;
        console.log('Regiao detectada:', this.regionConfig[region].label);
    }

    cacheRegion(region, metadata = {}) {
        try {
            localStorage.setItem(this.storageKey, JSON.stringify({
                region,
                state: metadata.state || '',
                zip: metadata.zip || '',
                source: metadata.source || 'unknown',
                timestamp: Date.now()
            }));
        } catch (error) {
            this.debug(error);
        }
    }

    getCachedRegion() {
        try {
            const rawValue = localStorage.getItem(this.storageKey);

            if (!rawValue) {
                return null;
            }

            const parsedValue = JSON.parse(rawValue);

            if (Date.now() - Number(parsedValue.timestamp || 0) > this.cacheDuration) {
                localStorage.removeItem(this.storageKey);
                return null;
            }

            return parsedValue;
        } catch (error) {
            try {
                localStorage.removeItem(this.storageKey);
            } catch (storageError) {
                this.debug(storageError);
            }
            return null;
        }
    }

    async detectRegionFromIp() {
        try {
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => controller.abort(), 3000);
            const response = await fetch('https://ipapi.co/json/', {
                signal: controller.signal,
                headers: {
                    Accept: 'application/json'
                }
            });

            window.clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error('IP lookup failed');
            }

            const data = await response.json();
            const countryCode = data?.country_code || data?.country_code_iso3 || data?.country;
            const state = data?.region_code || data?.state_code;

            if ((countryCode === 'BR' || countryCode === 'BRA') && state) {
                this.setRegionFromState(state, {
                    source: 'ip',
                    state
                });
                return;
            }
        } catch (error) {
            this.debug(error);
        }
        this.currentRegion = null;
        this.regionDetected = false;
        this.scheduleRender();
    }

    debug = () => {}
}

window.RegionalFreeShipping = window.RegionalFreeShipping || new RegionalFreeShipping();

const initializeRegionalFreeShipping = () => {
    window.RegionalFreeShipping.init();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeRegionalFreeShipping);
} else {
    initializeRegionalFreeShipping();
}

class ShippingRates extends HTMLElement {
    constructor() {
        super();

        this.form = null;
        this.ratesList = null;
        this.zipCode = null;
        this.button = null;
        this.message = null;
        this.submitButton = null;
        this.toggleInput = null;
        this.content = null;
        this.contentInner = null;
        this.location = this.getAttribute('data-location');
        this.initialized = false;
        this.pendingInitFrame = null;
        this.pendingHeightFrame = null;
        this.pendingObserverFrame = null;
        this.initObserver = null;
        this.contentResizeObserver = null;
        this.originalCartItems = [];
        this.invalidZipMessage = 'CEP inválido.';
        this.genericErrorMessage = 'Não conseguimos consultar, tente novamente ou entre em contato com nosso atendimento.';
        this.outOfAreaMessage = 'Ops, CEP fora da área de entrega. Confira se o CEP está preenchido corretamente ou entre em contato com nosso atendimento.';
    }

    debug = () => {}

    connectedCallback() {
        if (!this.refreshElements()) {
            this.scheduleInitializationRetry();
            this.startInitializationObserver();
            return;
        }

        if (this.pendingInitFrame !== null) {
            cancelAnimationFrame(this.pendingInitFrame);
            this.pendingInitFrame = null;
        }

        this.stopInitializationObserver();
        this.startContentResizeObserver();
        this.updateContentHeight();

        this.message.classList.remove('hidden');
        this.hideMessage();

        if (this.initialized) {
            return;
        }

        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleInput);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('keydown', this.handleKeydown);
        this.addEventListener('focusin', this.handleFocusIn);

        this.initialized = true;
    }

    disconnectedCallback() {
        if (this.pendingInitFrame !== null) {
            cancelAnimationFrame(this.pendingInitFrame);
            this.pendingInitFrame = null;
        }

        if (this.pendingHeightFrame !== null) {
            cancelAnimationFrame(this.pendingHeightFrame);
            this.pendingHeightFrame = null;
        }

        if (this.pendingObserverFrame !== null) {
            cancelAnimationFrame(this.pendingObserverFrame);
            this.pendingObserverFrame = null;
        }

        this.stopInitializationObserver();
        this.stopContentResizeObserver();

        if (!this.initialized) {
            return;
        }

        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('input', this.handleInput);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('keydown', this.handleKeydown);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.initialized = false;
    }

    scheduleInitializationRetry = () => {
        if (this.pendingInitFrame !== null) {
            return;
        }

        this.pendingInitFrame = requestAnimationFrame(() => {
            this.pendingInitFrame = null;

            if (!this.isConnected) {
                return;
            }

            this.connectedCallback();
        });
    }

    startInitializationObserver = () => {
        if (this.initObserver) {
            return;
        }

        this.initObserver = new MutationObserver(() => {
            if (!this.refreshElements()) {
                return;
            }

            this.connectedCallback();
        });

        this.initObserver.observe(this, {
            childList: true,
            subtree: true
        });
    }

    stopInitializationObserver = () => {
        if (!this.initObserver) {
            return;
        }

        this.initObserver.disconnect();
        this.initObserver = null;
    }

    startContentResizeObserver = () => {
        if (this.contentResizeObserver || !window.ResizeObserver || !this.contentInner) {
            return;
        }

        this.contentResizeObserver = new ResizeObserver(() => {
            if (this.pendingObserverFrame !== null) {
                return;
            }

            this.pendingObserverFrame = requestAnimationFrame(() => {
                this.pendingObserverFrame = null;
                this.updateContentHeight();
            });
        });

        this.contentResizeObserver.observe(this.contentInner);
    }

    stopContentResizeObserver = () => {
        if (!this.contentResizeObserver) {
            return;
        }

        this.contentResizeObserver.disconnect();
        this.contentResizeObserver = null;
    }

    updateContentHeight = () => {
        if (!this.content || !this.contentInner) {
            return;
        }

        const contentHeight = `${this.contentInner.scrollHeight}px`;
        const currentVar = this.content.style.getPropertyValue('--shipping-rates-content-height');

        if (currentVar !== contentHeight) {
            this.content.style.setProperty('--shipping-rates-content-height', contentHeight);
        }

        if (this.toggleInput?.checked && this.content.style.height !== contentHeight) {
            this.content.style.height = contentHeight;
        }
    }

    animateContentHeight = () => {
        if (!this.refreshElements()) {
            return;
        }

        const contentHeight = `${this.contentInner.scrollHeight}px`;
        this.content.style.setProperty('--shipping-rates-content-height', contentHeight);

        if (this.pendingHeightFrame !== null) {
            cancelAnimationFrame(this.pendingHeightFrame);
            this.pendingHeightFrame = null;
        }

        if (this.toggleInput.checked) {
            this.content.style.height = contentHeight;
            return;
        }

        this.content.style.height = `${this.content.getBoundingClientRect().height}px`;
        this.pendingHeightFrame = requestAnimationFrame(() => {
            this.pendingHeightFrame = null;
            this.content.style.height = '0px';
        });
    }

    refreshElements = () => {
        this.form = this.querySelector('[data-form="shipping-rates"]');
        this.ratesList = this.querySelector('[data-shipping-rates]');
        this.zipCode = this.querySelector('[data-zip-code]');
        this.button = this.querySelector('[data-form-show]');
        this.message = this.querySelector('[data-message]');
        this.zipCodeInput = this.querySelector('#zip-code');
        this.submitButton = this.querySelector('.btn-submit');
        this.toggleInput = this.querySelector('[data-shipping-toggle]');
        this.content = this.querySelector('.cart-drawer__shipping-form--content');
        this.contentInner = this.querySelector('.cart-drawer__shipping-form--content-inner');

        const hasAllElements = Boolean(
            this.form
            && this.ratesList
            && this.zipCode
            && this.button
            && this.message
            && this.zipCodeInput
            && this.submitButton
            && this.toggleInput
            && this.content
            && this.contentInner
        );

        return hasAllElements;
    }

    reset = () => {
        if (!this.refreshElements()) {
            return;
        }

        this.showElement(this.form);
        this.hideElement(this.ratesList);
        this.hideElement(this.zipCode);
        this.hideMessage();
    }

    handleInputInteraction = () => {
        if (!this.refreshElements()) {
            return;
        }

        this.hideMessage();
    }

    handleInputKeydown = (event) => {
        if (event.key !== 'Enter') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.submit();
    }

    handleSubmitAction = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.submit();
    }

    handleClick = (event) => {
        if (event.target.closest('.btn-submit')) {
            this.handleSubmitAction(event);
            return;
        }

        if (event.target.closest('[data-form-show]')) {
            this.reset();
            return;
        }

        if (event.target.closest('#zip-code')) {
            this.handleInputInteraction();
        }
    }

    handleInput = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.maskZipCode(event);
    }

    handleChange = (event) => {
        if (!event.target.matches('[data-shipping-toggle]')) {
            return;
        }

        this.animateContentHeight();
    }

    handleKeydown = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.handleInputKeydown(event);
    }

    handleFocusIn = (event) => {
        if (!event.target.matches('#zip-code')) {
            return;
        }

        this.handleInputInteraction();
    }

    submit = async () => {
        if (!this.refreshElements()) {
            return;
        }

        const buttonSubmit = this.submitButton;

        this.hideMessage();
        const zip = this.zipCodeInput?.value?.replace(/\D/g, '') || '';

        if (zip.length !== 8) {
            this.displayError(this.invalidZipMessage);
            return;
        }

        if (buttonSubmit) {
            buttonSubmit.setAttribute('disabled', 'true');
            buttonSubmit.innerHTML = 'Calculando...';
        }

        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v1/${zip}`);

            if (!response.ok) {
                if ([400, 404, 422, 504].includes(response.status)) {
                    throw new Error(this.invalidZipMessage);
                }

                throw new Error(this.genericErrorMessage);
            }

            const { state, cep } = await response.json();

            if (!state || !cep) {
                throw new Error(this.invalidZipMessage);
            }

            this.updateRegionalFreeShipping(state, cep);

            const shippingRates = await this.fetchShippingRates(state, cep);

            this.displayShippingRates(shippingRates);
            this.updateButton(cep);

            this.sendDataLayer({
                event: 'shipping_calculate',
                category: 'shipping',
                zipCode: cep,
                shippingRates: shippingRates.shipping_rates.map((shipping) => ({
                    label: shipping.name,
                    value: shipping.price,
                    currency: shipping.currency,
                    deadline: shipping.delivery_days?.[0] || null,
                    source: shipping.source,
                })),
                pageType: this.location,
                url: window.location.href
            });
        } catch (error) {
            this.displayError(error.message || this.genericErrorMessage);
        } finally {
            if (buttonSubmit) {
                buttonSubmit.removeAttribute('disabled');
                buttonSubmit.innerHTML = 'Calcular';
            }
        }
    }

    updateRegionalFreeShipping(state, zip) {
        if (!window.RegionalFreeShipping || !state) {
            return;
        }

        window.RegionalFreeShipping.setRegionFromState(state, {
            source: 'cep',
            state,
            zip
        });

        if (typeof window.RegionalFreeShipping.syncTotalsThenRender === 'function') {
            window.RegionalFreeShipping.syncTotalsThenRender();
        } else if (typeof window.RegionalFreeShipping.forceRender === 'function') {
            window.RegionalFreeShipping.forceRender();
        }
    }

    getSelectedVariantId() {
        return document.querySelector('form[action="/cart/add"] input[name="id"]')?.value
            || document.querySelector('input[name="id"]')?.value
            || null;
    }

    async saveCurrentCart() {
        const response = await fetch('/cart.js');
        const data = await response.json();
        this.originalCartItems = data.items || [];
    }

    async clearCart() {
        await fetch('/cart/clear.js', { method: 'POST' });
    }

    async restoreOriginalCart() {
        await this.clearCart();

        if (!this.originalCartItems.length) {
            return;
        }

        const items = this.originalCartItems.map((item) => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties || {}
        }));

        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items })
        });
    }

    async simulationCart(variantId, quantity = 1) {
        await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [{
                    id: variantId,
                    quantity
                }]
            })
        });
    }

    cartMatchesSimulation(variantId, quantity) {
        if (this.originalCartItems.length !== 1) {
            return false;
        }

        const item = this.originalCartItems[0];
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;

        return String(item.variant_id) === String(variantId)
            && item.quantity === quantity
            && !hasProperties;
    }

    updateButton(zip) {
        if (!this.refreshElements()) {
            return;
        }

        const container = this.zipCode;
        container.querySelector('span').textContent = zip;
        this.showElement(this.zipCode);
    }

    displayShippingRates(shippingRates) {
        if (!this.refreshElements()) {
            return;
        }

        if (!shippingRates?.shipping_rates?.length) {
            throw new Error(this.outOfAreaMessage);
        }

        const fragment = document.createDocumentFragment();
        shippingRates.shipping_rates.forEach((rate) => fragment.appendChild(this.createRateElement(rate)));

        this.ratesList.innerHTML = '';
        this.ratesList.appendChild(fragment);
        this.showElement(this.ratesList);
        this.hideElement(this.form);
    }

    createRateElement(rate) {
        const rateContainer = this.createElementWithClass('li', 'list-shipping-rates__item');
        const rateInfo = this.createElementWithClass('span', 'rate__info');
        const rateTitle = this.createElementWithClass('span', 'rate__title', rate.name);
        const hasDeadline = Boolean(rate.delivery_days?.[0]);
        const priceValue = this.parsePrice(rate.price);
        const isPaid = priceValue > 0;
        const deadlineText = hasDeadline
            ? `Em até ${rate.delivery_days[0]} dias úteis`
            : 'Confira a disponibilidade no checkout';
        const priceText = isPaid ? `R$ ${this.formatPrice(priceValue)}` : 'Frete grátis';
        const rateDeadline = this.createElementWithClass('span', 'rate__deadline', hasDeadline ? `${rate.delivery_days[0]} dias \u00fateis` : deadlineText);
        const ratePrice = this.createElementWithClass('span', 'rate__price', priceText);

        rateInfo.append(rateTitle, rateDeadline);
        rateContainer.append(rateInfo, ratePrice);

        return rateContainer;
    }

    formatPrice(value) {
        return this.parsePrice(value).toFixed(2).replace('.', ',');
    }

    parsePrice(value) {
        const normalized = String(value ?? '')
            .trim()
            .replace(/[^\d,.-]/g, '')
            .replace(',', '.');
        const parsed = Number(normalized);

        return Number.isFinite(parsed) ? parsed : 0;
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async fetchShippingRates(province, zip) {
        const queryParams = new URLSearchParams({
            'shipping_address[zip]': zip,
            'shipping_address[country]': 'BR',
            'shipping_address[province]': province
        });
        let lastError = new Error(this.genericErrorMessage);

        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const response = await fetch(`/cart/shipping_rates.json?${queryParams.toString()}`);

                if (!response.ok || response.status === 204) {
                    if (response.status === 422) {
                        throw new Error(this.outOfAreaMessage);
                    }

                    throw new Error(this.genericErrorMessage);
                }

                const data = await response.json();

                if (!data?.shipping_rates?.length) {
                    throw new Error(this.outOfAreaMessage);
                }

                return data;
            } catch (error) {
                if (error.message === this.outOfAreaMessage) {
                    throw error;
                }

                lastError = error;

                if (attempt === 2) {
                    throw lastError;
                }

                await this.delay(400);
            }
        }

        throw lastError;
    }

    createElementWithClass(tag, className, textContent = '') {
        const element = document.createElement(tag);
        element.classList.add(className);
        element.textContent = textContent;

        return element;
    }

    showElement(element) {
        element.classList.remove('hidden');
        requestAnimationFrame(this.updateContentHeight);
    }

    hideElement(element) {
        element.classList.add('hidden');
        requestAnimationFrame(this.updateContentHeight);
    }

    maskZipCode = (event) => {
        const input = event.target?.matches?.('#zip-code')
            ? event.target
            : this.zipCodeInput;

        if (!input || typeof input.value !== 'string') {
            return;
        }

        let cep = input.value.replace(/\D/g, '').slice(0, 8);

        if (cep.length > 5) {
            cep = cep.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
        }

        input.value = cep;
    }

    displayError(message) {
        if (!this.refreshElements()) {
            return;
        }

        this.message.textContent = message;
        this.showMessage();
    }

    showMessage() {
        if (!this.message) {
            return;
        }

        this.message.classList.add('is-visible');
        this.message.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(this.updateContentHeight);
    }

    hideMessage() {
        if (!this.message) {
            return;
        }

        this.message.classList.remove('is-visible');
        this.message.setAttribute('aria-hidden', 'true');
        requestAnimationFrame(this.updateContentHeight);
    }

    sendDataLayer(data) {
        const pubSubEvents = typeof PUB_SUB_EVENTS !== 'undefined'
            ? PUB_SUB_EVENTS
            : window.PUB_SUB_EVENTS;
        const eventName = pubSubEvents?.shippingCalculate || data?.event || 'shipping_calculate';

        if (window.Shopify?.analytics?.publish) {
            window.Shopify.analytics.publish(eventName, data);
            return;
        }

        if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push(data);
        }
    }
}

if (!customElements.get('shipping-rates')) {
    customElements.define('shipping-rates', ShippingRates);
}
